import { useState, useEffect, useCallback } from 'react';
import { MdCheckCircle, MdCancel, MdRadioButtonUnchecked, MdFilterList, MdRefresh, MdExpandMore, MdExpandLess, MdBugReport, MdStorage, MdCloud } from 'react-icons/md';
import api from '../../services/api';

const STORAGE_KEY = 'ta_launch_v1';

const SECTIONS = [
  {
    id: 'login',
    title: 'Login & Roles',
    items: [
      { id: 'admin-login',    title: 'Admin login works',                          note: 'Log in at /login with an admin account. Confirm dashboard loads.' },
      { id: 'trainer-login',  title: 'Trainer login works',                        note: 'Log in with a trainer account. Confirm trainer dashboard loads.' },
      { id: 'student-login',  title: 'Student login works',                        note: 'Log in with a student account. Confirm student dashboard loads.' },
      { id: 'sa-login',       title: 'Superadmin login works',                     note: 'Log in at /superadmin. Confirm clubs list and audit logs load.' },
      { id: 'role-guard',     title: 'Wrong role cannot access wrong dashboard',   note: 'Visit /admin/dashboard as a trainer or student. Should redirect to their own portal.' },
    ],
  },
  {
    id: 'club-setup',
    title: 'Club Setup',
    items: [
      { id: 'club-profile',   title: 'Club profile completed',                     note: 'Go to Club Profile. Add phone, address, logo. Confirm completion banner updates.' },
      { id: 'belts-setup',    title: 'Belts/grading configured',                   note: 'Go to Belt Exams. Schedule an exam. Confirm it appears on dashboard checklist.' },
      { id: 'class-created',  title: 'Class created',                              note: 'Go to Classes. Add a class with days and trainer. Confirm it appears in list.' },
      { id: 'trainer-added',  title: 'Trainer invited or created',                 note: 'Go to Trainers. Invite a trainer via email. Confirm invite email arrives.' },
      { id: 'student-added',  title: 'Student added',                              note: 'Go to Students. Add student directly. Confirm student appears in list.' },
      { id: 'intake-works',   title: 'Website Requests page works',                note: 'Go to Website Requests. Confirm page loads with filter, table, and add button.' },
    ],
  },
  {
    id: 'website-requests',
    title: 'Website Requests',
    items: [
      { id: 'add-trial',      title: 'Add website request',                        note: 'Click Add Request. Fill name, phone, date. Confirm appears in table.' },
      { id: 'followup-note',  title: 'Follow-up note works',                       note: 'Open a request row. Add a follow-up note. Confirm it saves and appears.' },
      { id: 'contact-actions',title: 'WhatsApp / email / phone actions work',      note: 'Click WhatsApp / Email / Phone icons on a request row. Confirm correct links open.' },
      { id: 'convert-trial',  title: 'Convert request → student',                  note: 'Click Convert on a request. Complete the wizard. Confirm account is created.' },
      { id: 'converted-shows',title: 'Converted student appears in Students',      note: 'After conversion, go to Students. Confirm the converted student is visible.' },
    ],
  },
  {
    id: 'full-students',
    title: 'Full Students',
    items: [
      { id: 'search-student', title: 'Search student',                             note: 'Type a student name in search. Confirm table filters correctly in real time.' },
      { id: 'filter-student', title: 'Filter by status / belt / payment',          note: 'Use the filter dropdowns. Confirm results change. Clear filters to reset.' },
      { id: 'open-drawer',    title: 'Open student drawer',                        note: 'Click a student row. Confirm drawer opens with profile, payments, and actions.' },
      { id: 'record-drawer',  title: 'Record payment from drawer',                 note: 'In the student drawer, click Record Payment. Enter amount and confirm save.' },
      { id: 'download-pdf',   title: 'Download student PDF report',                note: 'In the student drawer, click Export / Download PDF. Confirm PDF opens.' },
      { id: 'archive-student',title: 'Archive and restore student',                note: 'Archive a student from the drawer. Go to Archived tab. Confirm visible. Restore.' },
    ],
  },
  {
    id: 'attendance',
    title: 'Attendance',
    items: [
      { id: 'session-attend', title: 'Create session attendance',                  note: 'Go to Sessions. Open a session. Click Take Attendance. Confirm attendance modal opens.' },
      { id: 'mark-student',   title: 'Mark student present / late / absent',       note: 'In attendance modal, mark each status. Save. Confirm records are stored.' },
      { id: 'attend-badge',   title: 'Attendance badge updates',                   note: 'After marking, check student profile for attendance rate change.' },
      { id: 'trainer-attend', title: 'Trainer attendance workflow works',          note: 'Log in as trainer. Go to Sessions → take attendance. Confirm it saves correctly.' },
    ],
  },
  {
    id: 'monthly-fees',
    title: 'Monthly Fees',
    items: [
      { id: 'set-fee',        title: 'Set monthly fee amount',                     note: 'Go to Payments → Overview → Fee Structure. Set monthly fee. Save.' },
      { id: 'add-qr',         title: 'Add eSewa / Khalti / Fonepay / Bank QR',    note: 'Go to Payments → Overview → Nepal Payment Methods. Upload a QR image or add an ID.' },
      { id: 'generate-fees',  title: 'Generate monthly fees',                      note: 'Go to Monthly Fees tab. Click Generate Monthly Fees. Confirm fee rows appear.' },
      { id: 'record-manual',  title: 'Record payment manually',                    note: 'Click Record Payment Manually on a fee row. Fill details. Confirm status changes.' },
      { id: 'badge-paid',     title: 'Full Students badge becomes Paid',           note: 'After recording payment, open Full Students. Confirm payment badge shows Paid.' },
      { id: 'late-fee',       title: 'Overdue fee shows correctly',                note: 'Change month to a past month. Confirm overdue fees appear with red badge.' },
    ],
  },
  {
    id: 'parent-payment',
    title: 'Parent Payment Workflow',
    items: [
      { id: 'copy-link',      title: 'Copy parent payment link',                   note: 'On a pending fee row, click the link icon. Confirm link is copied to clipboard.' },
      { id: 'incognito-open', title: 'Open link in incognito without login',       note: 'Paste the copied link in incognito. Confirm page loads without requiring login.' },
      { id: 'view-qr',        title: 'View QR codes on payment page',              note: 'On the parent payment page, confirm eSewa/Khalti/Fonepay QR images are visible.' },
      { id: 'upload-proof',   title: 'Upload payment screenshot',                  note: 'On the parent payment page, upload a screenshot. Confirm upload success message.' },
      { id: 'admin-sees',     title: 'Admin sees uploaded proof',                  note: 'Log in as admin. Go to Payments. Find the fee row. Confirm proof_submitted badge.' },
      { id: 'admin-approves', title: 'Admin approves proof — fee becomes Paid',   note: 'Click Approve on the proof. Confirm fee status changes to Completed/Paid.' },
      { id: 'admin-rejects',  title: 'Admin rejects proof with reason',            note: 'On another fee, reject the proof. Type a reason. Confirm rejection is recorded.' },
      { id: 'parent-reject',  title: 'Parent sees rejection reason',               note: 'Open the payment link again. Confirm rejection reason is shown on the page.' },
    ],
  },
  {
    id: 'reminders',
    title: 'Reminders',
    items: [
      { id: 'copy-reminder',  title: 'Copy reminder message',                      note: 'On a pending fee, click Copy Reminder. Confirm formatted message is in clipboard.' },
      { id: 'whatsapp',       title: 'Open WhatsApp reminder',                     note: 'Click WhatsApp icon on a pending fee. Confirm WhatsApp opens with pre-filled message.' },
      { id: 'email-reminder', title: 'Send email reminder',                        note: 'Click Email icon on a pending fee. Confirm email is sent. Check recipient inbox.' },
      { id: 'bulk-copy',      title: 'Bulk copy reminders',                        note: 'Set filter to Pending. Click Copy All Reminders. Confirm all messages are copied.' },
      { id: 'bulk-email',     title: 'Bulk email reminders',                       note: 'Set filter to Pending. Click Email All Reminders. Confirm bulk send success toast.' },
    ],
  },
  {
    id: 'expenses',
    title: 'Expenses',
    items: [
      { id: 'image-receipt',  title: 'Admin uploads image receipt',                note: 'Go to Expenses. Add an expense. Upload a JPG/PNG receipt. Confirm upload works.' },
      { id: 'pdf-receipt',    title: 'Admin uploads PDF receipt',                  note: 'Add another expense. Upload a PDF receipt. Confirm View button opens the PDF.' },
      { id: 'trainer-expense',title: 'Trainer submits expense',                    note: 'Log in as trainer. Go to Club Expenses. Submit an expense. Confirm pending status.' },
      { id: 'approve-expense',title: 'Admin approves or rejects expense',          note: 'Log in as admin. Open pending expense. Approve or reject. Confirm status changes.' },
      { id: 'receipt-deploy', title: 'Receipt opens after Railway redeploy',       note: 'After redeploying backend, open an old receipt. Confirm URL still works (Cloudinary).' },
    ],
  },
  {
    id: 'reports',
    title: 'Reports',
    items: [
      { id: 'export-students',title: 'Export student list PDF',                    note: 'Go to Reports. Click Export Students. Confirm PDF downloads with student data.' },
      { id: 'export-profile', title: 'Export student profile PDF',                 note: 'Open a student drawer. Click Download PDF. Confirm individual profile PDF.' },
      { id: 'export-expenses',title: 'Export expense report',                      note: 'Go to Expenses. Click Export PDF. Confirm expense report PDF downloads.' },
      { id: 'data-matches',   title: 'Report data matches page data',              note: 'Compare exported PDF totals with what is shown on the Payments/Expenses pages.' },
    ],
  },
  {
    id: 'mobile-dark',
    title: 'Mobile & Dark Mode',
    items: [
      { id: 'admin-mobile',   title: 'Admin dashboard mobile',                     note: 'Open admin dashboard on a phone or DevTools at 390px. Check layout, sidebar, cards.' },
      { id: 'payment-mobile', title: 'Monthly Fees page mobile',                   note: 'Open Payments on mobile. Check table scrolling, action buttons, row actions.' },
      { id: 'parent-mobile',  title: 'Parent payment page mobile',                 note: 'Open a parent payment link on a phone. Confirm QR images and upload button work.' },
      { id: 'expenses-mobile',title: 'Expenses page mobile',                       note: 'Open Expenses on mobile. Check that receipt upload and approval actions are usable.' },
      { id: 'dark-mode',      title: 'Dark mode readability',                      note: 'Toggle dark mode in Settings. Check all pages for unreadable text or broken contrast.' },
    ],
  },
  {
    id: 'security',
    title: 'Security & Tenant Safety',
    items: [
      { id: 'cross-tenant',   title: 'Club A cannot see Club B data',              note: 'Log in as admin of Club A. Check that all API calls return only Club A data.' },
      { id: 'trainer-guard',  title: 'Trainer cannot access admin-only pages',     note: 'Log in as trainer. Manually visit /admin/payments. Should redirect or deny.' },
      { id: 'student-guard',  title: 'Student cannot access admin pages',          note: 'Log in as student. Visit /admin/dashboard. Should redirect to student portal.' },
      { id: 'public-link',    title: 'Public payment link shows only safe data',   note: 'On the parent payment page, confirm only student name and fee amount are visible.' },
      { id: 'expired-link',   title: 'Expired/invalid payment link shows safe error', note: 'Modify a payment token URL manually. Confirm a safe error page is shown — no crash.' },
    ],
  },
];

const STATUS = {
  not_tested: { label: 'Not Tested', color: 'text-gray-400',  bg: 'bg-gray-100 dark:bg-gray-800' },
  passed:     { label: 'Passed',     color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' },
  failed:     { label: 'Failed',     color: 'text-red-600',   bg: 'bg-red-50 dark:bg-red-900/20' },
};

function loadState() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; }
  catch { return {}; }
}

function saveState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

const HEALTH_ROWS = [
  { key: 'server',   label: 'Backend Server', Icon: MdBugReport },
  { key: 'database', label: 'Database',        Icon: MdStorage   },
];

function SystemHealth() {
  const [health,   setHealth]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(false);

  const fetch = useCallback(() => {
    setLoading(true);
    setError(false);
    api.get('/api/health')
      .then(r => { setHealth(r.data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const checks  = health?.checks || {};
  const allOk   = health?.status === 'ok';

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl mb-5 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-secondary">System Health</span>
          {!loading && !error && (
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${allOk ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {allOk ? 'All OK' : 'Degraded'}
            </span>
          )}
          {error && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">
              Cannot reach server
            </span>
          )}
        </div>
        <button
          onClick={fetch}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-primary transition-colors"
        >
          <MdRefresh className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100 dark:divide-gray-700">
        {HEALTH_ROWS.map(({ key, label, Icon }) => {
          const ok = checks[key];
          return (
            <div key={key} className="flex flex-col items-center gap-1.5 px-4 py-3">
              <Icon className="text-gray-300 text-lg" />
              <span className="text-xs text-gray-500">{label}</span>
              {loading ? (
                <div className="w-12 h-4 bg-gray-100 rounded animate-pulse" />
              ) : error ? (
                <span className="text-[10px] text-red-400 font-semibold">Error</span>
              ) : (
                <span className={`text-[10px] font-bold ${ok ? 'text-green-600' : 'text-red-500'}`}>
                  {ok ? '✓ OK' : '✗ Missing'}
                </span>
              )}
            </div>
          );
        })}
        <div className="flex flex-col items-center gap-1.5 px-4 py-3 col-span-2 sm:col-span-2">
          <MdCloud className="text-gray-300 text-lg" />
          <span className="text-xs text-gray-500">Cloudinary &amp; Email</span>
          <span className="text-[10px] text-gray-400 text-center">Verify in Railway env vars</span>
        </div>
      </div>
    </div>
  );
}

export default function LaunchTesting() {
  const [state,       setState]       = useState(loadState);
  const [filter,      setFilter]      = useState('all'); // 'all' | 'failed' | 'passed' | 'not_tested'
  const [collapsed,   setCollapsed]   = useState({});
  const [activeNote,  setActiveNote]  = useState(null); // item id with open note input

  const allItems = SECTIONS.flatMap(s => s.items);
  const total    = allItems.length;
  const passed   = allItems.filter(i => state[i.id]?.status === 'passed').length;
  const failed   = allItems.filter(i => state[i.id]?.status === 'failed').length;
  const tested   = passed + failed;
  const pct      = total > 0 ? Math.round((tested / total) * 100) : 0;

  const setStatus = useCallback((id, status) => {
    setState(prev => {
      const next = { ...prev, [id]: { ...prev[id], status } };
      saveState(next);
      return next;
    });
    if (status !== 'failed') setActiveNote(n => n === id ? null : n);
  }, []);

  const setNote = useCallback((id, note) => {
    setState(prev => {
      const next = { ...prev, [id]: { ...prev[id], note } };
      saveState(next);
      return next;
    });
  }, []);

  const resetAll = () => {
    setState({});
    saveState({});
    setActiveNote(null);
  };

  const toggleSection = (id) =>
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  const visibleSections = SECTIONS.map(section => ({
    ...section,
    items: filter === 'all'
      ? section.items
      : section.items.filter(item => (state[item.id]?.status || 'not_tested') === filter),
  })).filter(s => s.items.length > 0);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-secondary flex items-center gap-2">
              <MdBugReport className="text-red-500 text-2xl" />
              Launch Testing Checklist
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Internal pre-launch testing guide — {total} items across {SECTIONS.length} areas.
              Progress is saved in this browser only.
            </p>
          </div>
          <button
            onClick={resetAll}
            className="flex items-center gap-1.5 text-xs text-gray-400 border border-gray-200 px-3 py-1.5 rounded-lg hover:border-red-300 hover:text-red-500 transition-colors"
          >
            <MdRefresh /> Reset All
          </button>
        </div>
      </div>

      {/* System Health */}
      <SystemHealth />

      {/* Progress bar */}
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-5 mb-5 shadow-sm">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-5">
            <span className="text-sm font-semibold text-secondary">{pct}% Tested</span>
            <span className="text-xs text-green-600 font-medium flex items-center gap-1">
              <MdCheckCircle className="text-base" /> {passed} Passed
            </span>
            <span className="text-xs text-red-500 font-medium flex items-center gap-1">
              <MdCancel className="text-base" /> {failed} Failed
            </span>
            <span className="text-xs text-gray-400">
              {total - tested} not tested
            </span>
          </div>
          <span className="text-xs text-gray-400">{tested} / {total} items</span>
        </div>
        <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
          <div className="flex h-full rounded-full overflow-hidden">
            {passed > 0 && (
              <div
                className="bg-green-500 h-full transition-all duration-300"
                style={{ width: `${(passed / total) * 100}%` }}
              />
            )}
            {failed > 0 && (
              <div
                className="bg-red-400 h-full transition-all duration-300"
                style={{ width: `${(failed / total) * 100}%` }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <MdFilterList className="text-gray-400 flex-shrink-0" />
        {[
          { key: 'all',       label: 'All' },
          { key: 'not_tested',label: 'Not Tested' },
          { key: 'passed',    label: 'Passed' },
          { key: 'failed',    label: `Failed${failed > 0 ? ` (${failed})` : ''}` },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors border ${
              filter === key
                ? key === 'failed'
                  ? 'bg-red-50 border-red-200 text-red-600'
                  : key === 'passed'
                    ? 'bg-green-50 border-green-200 text-green-600'
                    : 'bg-primary border-primary text-white'
                : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
        {filter !== 'all' && (
          <span className="text-xs text-gray-400 ml-1">
            Showing {visibleSections.reduce((n, s) => n + s.items.length, 0)} items
          </span>
        )}
      </div>

      {/* Sections */}
      {visibleSections.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <MdCheckCircle className="text-5xl text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-semibold text-gray-500">
            {filter === 'failed' ? 'No failed items — great!' : 'No items match this filter.'}
          </p>
        </div>
      ) : (
        visibleSections.map(section => {
          const sItems      = section.items;
          const sPassed     = sItems.filter(i => state[i.id]?.status === 'passed').length;
          const sFailed     = sItems.filter(i => state[i.id]?.status === 'failed').length;
          const isCollapsed = collapsed[section.id];

          return (
            <div key={section.id} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl mb-4 shadow-sm overflow-hidden">
              {/* Section header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-secondary text-sm">{section.title}</span>
                  <div className="flex items-center gap-2">
                    {sPassed > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                        {sPassed} passed
                      </span>
                    )}
                    {sFailed > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">
                        {sFailed} failed
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400">
                      {sItems.length} items
                    </span>
                  </div>
                </div>
                {isCollapsed ? <MdExpandMore className="text-gray-400" /> : <MdExpandLess className="text-gray-400" />}
              </button>

              {/* Items */}
              {!isCollapsed && (
                <div className="border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-700">
                  {sItems.map(item => {
                    const itemState = state[item.id] || {};
                    const status    = itemState.status || 'not_tested';
                    const note      = itemState.note   || '';
                    const noteOpen  = activeNote === item.id;

                    return (
                      <div
                        key={item.id}
                        className={`px-5 py-3.5 transition-colors ${STATUS[status].bg}`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Status icon */}
                          <div className="flex-shrink-0 mt-0.5">
                            {status === 'passed'
                              ? <MdCheckCircle className="text-green-500 text-xl" />
                              : status === 'failed'
                                ? <MdCancel className="text-red-500 text-xl" />
                                : <MdRadioButtonUnchecked className="text-gray-300 text-xl" />
                            }
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium leading-snug ${status === 'passed' ? 'text-green-700 line-through decoration-green-400/60' : status === 'failed' ? 'text-red-700' : 'text-secondary'}`}>
                              {item.title}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{item.note}</p>

                            {/* Bug note input — shown when failed or when explicitly opened */}
                            {(status === 'failed' || noteOpen) && (
                              <div className="mt-2">
                                <textarea
                                  value={note}
                                  onChange={e => setNote(item.id, e.target.value)}
                                  placeholder="Describe what went wrong…"
                                  rows={2}
                                  className="w-full text-xs border border-red-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:border-red-400 bg-white dark:bg-gray-900 dark:border-red-800 text-secondary placeholder-gray-300"
                                />
                              </div>
                            )}
                          </div>

                          {/* Action buttons */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => setStatus(item.id, status === 'passed' ? 'not_tested' : 'passed')}
                              title="Mark Passed"
                              className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg transition-colors border ${
                                status === 'passed'
                                  ? 'bg-green-500 border-green-500 text-white'
                                  : 'border-gray-200 text-gray-400 hover:border-green-400 hover:text-green-600'
                              }`}
                            >
                              <MdCheckCircle className="text-sm" />
                              <span className="hidden sm:inline">Pass</span>
                            </button>
                            <button
                              onClick={() => {
                                setStatus(item.id, status === 'failed' ? 'not_tested' : 'failed');
                                if (status !== 'failed') setActiveNote(item.id);
                              }}
                              title="Mark Failed"
                              className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg transition-colors border ${
                                status === 'failed'
                                  ? 'bg-red-500 border-red-500 text-white'
                                  : 'border-gray-200 text-gray-400 hover:border-red-400 hover:text-red-500'
                              }`}
                            >
                              <MdCancel className="text-sm" />
                              <span className="hidden sm:inline">Fail</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Footer */}
      <div className="mt-6 mb-10 text-center text-xs text-gray-300">
        Progress saved in this browser only · Not shared with other team members · Reset to start a fresh test run
      </div>
    </div>
  );
}
